import { Form } from "react-bootstrap";
import {
  Box,
  FileUploaderStyled,
  LazyImage,
  SpanStyled,
} from "../../../../../components/styled/Elements";
import {
  FileUpload,
  TrashIcon,
} from "../../../../../components/styled/AllImages";
import { useTranslation } from "react-i18next";
import { useFormikContext } from "formik";
import { useRef, useState } from "react";
import FileUploadApi from "../../../../../services/api/fileUploadApi";
import axios from "axios";
import FileUploadProgress from "../../../../../components/common/uploadFiles/UploadProgress";
import styled from "styled-components";
import { uploadFileToServer } from "../../../../../helpers/uploadFileHelper";

const FileAttachment = () => {
  const { t } = useTranslation();
  const { setFieldValue, values } = useFormikContext();

  const fileInputRef = useRef();

  const [progress, setProgress] = useState(0);
  const [previewImg, setPreviewImg] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const getUploadSettings = () => {
    switch (values.archive_type_id) {
      case "2":
        return {
          accept: "video/*",
          uploadText: "UploadVideo",
        };
      case "3":
        return {
          accept: "audio/*",
          uploadText: "UploadVoice",
        };
      case "4":
        return {
          accept: "image/*",
          uploadText: "UploadImage",
        };
      case "5":
        return {
          accept: "*",
          uploadText: "upload",
        };
      default:
        return {
          accept:
            ".pdf, .doc, .docx, .txt, .odt, .ppt, .pptx, .xls, .xlsx, .rtf, .csv, .ods, .odp, .epub",
          uploadText: "UploadDocument",
        };
    }
  };
  const { accept, uploadText } = getUploadSettings();

  const finalizeMultipartUpload = async (fileName, uploadId, parts) => {
    try {
      const response = await FileUploadApi.finalizeUpload({
        fileName,
        uploadId,
        parts,
      });
      return response.data.fileUrl;
    } catch (error) {
      console.error("Error finalizing multipart upload:", error);
      throw error;
    }
  };

  const handleFileChange = async (e) => {
    const { files } = e.target;
    if (!files[0]) return;

    const file = files[0];
    const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB max file size

    const objUrl = URL.createObjectURL(file);
    setPreviewImg({ url: objUrl, type: file.type, name: file.name });

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      console.error("File size exceeds maximum limit of 100MB");
      return;
    }

    try {
      setIsUploading(true);
      setProgress(0);

      const formData = new FormData();
      formData.append("file", file);
      formData.append("fileName", file.name);

      // Upload file directly to backend
      const response = await uploadFileToServer(formData, (progressEvent) => {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total,
        );
        setProgress(percentCompleted);
      });


      if (response?.data) {
        setFieldValue("attachment", file);
        setFieldValue("finalizedFileUrl", response.data?.filePath);
        setIsUploading(false);
        setProgress(0);
      }
    } catch (error) {
      console.error("File upload failed:", error);
      setIsUploading(false);
      setProgress(0);
    }
  };

  const generatePresignedUrl = async (fileName, chunkIndex, uploadId) => {
    const formData = {
      fileName,
      chunkIndex,
    };
    if (uploadId) {
      formData.uploadId = uploadId;
    }
    try {
      const response = await FileUploadApi.getPresignedUrl(formData);
      return response;
    } catch (error) {
      return;
    }
  };

  const uploadChunk = async (presignedUrl, chunk, totalChunks, i, parts) => {
    try {
      const response = await axios.put(presignedUrl, chunk, {
        headers: {
          "Content-Type": "application/octet-stream",
        },
      });
      parts.push({
        PartNumber: i + 1,
        ETag: response.headers.etag,
      });
      setProgress(Math.round((parts.length / totalChunks) * 100));
    } catch (error) {
      return;
    }
  };

  return (
    <ControllerWrapper>
      <FileUploaderStyled className="d-flex align-items-center justify-content-center w-100">
        <Form
          as="input"
          name="attachment"
          type="file"
          accept={accept}
          ref={fileInputRef}
          disabled={isUploading}
          onChange={(e) => {
            handleFileChange(e);
          }}
          className="d-none"
        />
        {previewImg ? (
          <Box
            className="d-flex align-items-center justify-content-between w-100 mt-n4 preview-box"
            onClick={(e) => {
              if (previewImg) {
                e.stopPropagation();
                e.preventDefault();
              }
            }}
          >
            {previewImg?.type.startsWith("image/") && (
              <div className="job-upload-preview-img">
                <LazyImage src={previewImg?.url} />
              </div>
            )}
            {previewImg.type.startsWith("video/") && (
              <video
                src={previewImg.url}
                className="preview-video"
                // poster={previewImg.url}
                muted
                controls={false} // Disable controls
                onClick={(e) => e.preventDefault()}
              />
            )}
            {previewImg.type.startsWith("audio/") && (
              <Box className="file-preview">
                <SpanStyled>
                  {t("audio_file_selected")}: {previewImg.name}
                </SpanStyled>
              </Box>
            )}
            {!previewImg?.type.startsWith("image/") &&
              !previewImg?.type.startsWith("video/") &&
              !previewImg?.type.startsWith("audio/") && (
                <Box className="file-preview">
                  <SpanStyled>{previewImg?.name}</SpanStyled>
                </Box>
              )}
            <Box className="d-flex align-items-center flex-column gap-1">
              <span
                className="default-icon delete-icon"
                onClick={(e) => {
                  if (isUploading) return;
                  e.stopPropagation();
                  e.preventDefault();
                  setPreviewImg(null);
                  setFieldValue("attachment", null);
                  fileInputRef.current.value = "";
                }}
              >
                <TrashIcon />
              </span>
            </Box>
          </Box>
        ) : values.attachment ? (
          <>
            <Box className="file-preview">
              <SpanStyled>{values.attachment.split("/").pop()}</SpanStyled>
            </Box>
            <Box className="d-flex align-items-center flex-column gap-1">
              <span
                className="default-icon delete-icon"
                onClick={(e) => {
                  if (isUploading) return;
                  e.stopPropagation();
                  e.preventDefault();
                  setPreviewImg(null);
                  setFieldValue("attachment", null);
                  fileInputRef.current.value = "";
                }}
              >
                <TrashIcon />
              </span>
            </Box>
          </>
        ) : (
          <>
            <Box className="d-flex align-items-center justify-content-center mx-5 w-100">
              <FileUpload size={26} />
              &nbsp;&nbsp;
              <SpanStyled color="var(--text)" size="14px">
                {t(uploadText)}
              </SpanStyled>
            </Box>
          </>
        )}
      </FileUploaderStyled>
      {isUploading && <FileUploadProgress progress={progress} />}
    </ControllerWrapper>
  );
};

export default FileAttachment;

const ControllerWrapper = styled.div`
  .preview-box {
    position: relative;
    width: 100%;
    height: 80px;
    z-index: 999;

    .default-icon.delete-icon {
      svg:hover {
        [stroke] {
          stroke: var(--redColor);
        }
      }
    }
  }

  .preview-video {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }

  .preview-img {
    width: 230px;
    height: 80px;
    object-fit: cover;
  }
  .job-upload-preview-img {
    width: 230px;
    height: 80px;
    border-radius: 10px;
  }
  .job-upload-preview-img span {
    width: 100%;
    height: 100%;
    display: flex !important;
    justify-content: center;
    align-items: center;
    border-radius: 10px;
  }
  .job-upload-preview-img span img {
    max-width: 100%;
    max-height: 100%;
    border-radius: 10px;
  }
`;
